import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// Direct API call without interceptor (to avoid circular dependency)
const verifyToken = async (token) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  console.log('[Auth] Verifying token with API:', API_URL);
  
  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    console.log('[Auth] Verify response status:', response.status);
    
    if (!response.ok) {
      // Don't immediately clear session on network errors or 5xx
      if (response.status >= 500) {
        console.warn('[Auth] Server error during verification, keeping session');
        // Return cached user data if available
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          return JSON.parse(cachedUser);
        }
      }
      const errorText = await response.text();
      console.error('[Auth] Verify failed:', response.status, errorText);
      throw new Error(`Token invalid: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('[Auth] Verify success, user:', data.username);
    return data;
  } catch (error) {
    // Network errors - don't clear session, use cached data
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.warn('[Auth] Network error during verification, using cached session');
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        return JSON.parse(cachedUser);
      }
    }
    console.error('[Auth] Verify error:', error.message);
    throw error;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    let isMounted = true;
    
    const verify = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        if (isMounted) setLoading(false);
        return;
      }
      
      try {
        const userData = await verifyToken(storedToken);
        if (isMounted) {
          setUser(userData);
          setToken(storedToken);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (error) {
        console.warn('Token verification failed, clearing session');
        if (isMounted) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    verify();
    
    return () => { isMounted = false; };
  }, []);

  const login = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = !!token && !!user;
  const isOwner = user?.is_owner === true;

  // Permission check helper
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.is_owner) return true;
    
    // Convert permission key (replace all dots with underscores)
    const permKey = permission.replace(/\./g, '_');
    return user.permissions?.[permKey] === true;
  };

  // Check multiple permissions (any)
  const hasAnyPermission = (permissions) => {
    return permissions.some(p => hasPermission(p));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      isAuthenticated, 
      isOwner, 
      hasPermission,
      hasAnyPermission,
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
